import { Dialog, FormControlLabel } from "@mui/material";
import { ArrowLeft, Plus, X } from "feather-icons-react/build/IconComponents";
import { t } from "i18next";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import API from "../../API/newIndex";
import { TableColumnsContext, ThemeContext } from "../../App";
import Breadcrumbs from "../../controllers/breadCrumbs";
import { formatDate } from "../../controllers/FormatAmount";
import MuiSwitch from "../../controllers/MuiSwitch";
import { getInitialPage, replacePageInUrl } from "../../controllers/PageHandleFromQuery";
import { parentNavigate } from "../../controllers/ParentNavigate";
import { Transition } from "../../controllers/Transition";
import { useUser } from "../../controllers/UserContext";
import Notification from "../Notification";
import SendMail from "./SendMail";
import {
  ButtonComponent,
  CommonTable,
  PaginationComponent,
  SelectComponent,
  InputComponent,
} from "@shopeasy-dashboard-ui/ui-library";
import { isAscendingOrder } from "../../utilities/staticFunction";
import * as defaultCustomFields from '../../utilities/defaultCustomTableField';

function GetMails() {
  const location = useLocation();
  const [allMail, setAllMail] = useState();
  const [pending, setPending] = useState(true);

  const { sortData, isUserLoaded } = useUser();
  const userContext = useUser();

  const savedSort = sortData?.newsletter || sortData?.newsletters || sortData?.subscribers;

  const [sortBy, setSortBy] = useState(
    () => (location?.state?.accending ? location?.state?.field : savedSort?.sort_by || "createdAt")
  );
  const [orderBy, setOrderBy] = useState(() => {
    if (location?.state?.accending) {
      return { orderByValue: "ASC", isDecending: false };
    }
    const isAsc = isAscendingOrder(savedSort?.order_by);
    return {
      orderByValue: savedSort?.order_by ? (isAsc ? "ASC" : "DESC") : "DESC",
      isDecending: savedSort?.order_by ? !isAsc : true,
    };
  });
  const isInitialSortLoaded = useRef(Boolean(location?.state?.field || savedSort?.sort_by));

  const [page, setPage] = useState(getInitialPage());
  const [limit, setLimit] = useState(10);
  const [status, setStatus] = useState("all");
  const [isNotification, setIsNotification] = useState(false);
  const [notificationMsg, setNotificationMsg] = useState("");
  const [severity, setSeverity] = useState();
  const [openSendMail, setOpenSendMail] = useState(false);
  const [openAddSubscriber, setOpenAddSubscriber] = useState(false);
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [addLoading, setAddLoading] = useState(false);

  const { tableColumnsData } = useContext(TableColumnsContext);

  const apiFieldNames = useMemo(() => {
    const dataObj = tableColumnsData?.data?.data || tableColumnsData?.data || tableColumnsData;
    if (Array.isArray(dataObj?.newsletter)) return dataObj.newsletter;
    if (Array.isArray(dataObj?.newsletters)) return dataObj.newsletters;
    if (Array.isArray(dataObj?.subscribers)) return dataObj.subscribers;
    if (Array.isArray(tableColumnsData?.newsletter)) return tableColumnsData.newsletter;
    if (Array.isArray(tableColumnsData?.newsletters)) return tableColumnsData.newsletters;
    return [];
  }, [tableColumnsData]);

  const dynamicColumns = useMemo(() => {
    const fixedKeys = new Set([
      "_id",
      "__v",
      "client",
      "tenant",
      "store_client",
      "store_tenant",
      "user",
      "newsletter_email",
      "createdAt",
      "updatedAt",
      "subscribe",
      "action",
      "actions",
      "checkbox",
      "selection",
      "select",
      "drag_handle",
      "draghandle",
      "drag",
    ]);

    return apiFieldNames
      .filter((fieldName) => typeof fieldName === "string" && fieldName.trim() && !fixedKeys.has(fieldName))
      .map((fieldName) => {
        const isDotted = fieldName.includes(".");
        const parts = isDotted ? fieldName.split(".") : null;

        const label = fieldName
          .replace(/\./g, " › ")
          .replace(/[-_]/g, " ")
          .replace(/([a-z])([A-Z])/g, "$1 $2")
          .replace(/\b\w/g, (c) => c.toUpperCase());

        const getValue = (row) => {
          if (!row) return undefined;
          if (isDotted && parts) {
            return parts.reduce((obj, k) => (obj != null ? obj[k] : undefined), row);
          }
          return row[fieldName];
        };

        return {
          key: fieldName,
          title: label,
          width: "160px",
          minWidth: "110px",
          responsiveWidth: "100px",
          headerClassName: "px-3 whitespace-nowrap truncate",
          cellClassName: "px-3 truncate",
          render: (row) => {
            const val = getValue(row);
            if (val === null || val === undefined || val === "") return "-";
            if (typeof val === "boolean") return val ? "Yes" : "No";
            if (fieldName.toLowerCase().includes("date") && typeof val === "string") {
              return formatDate(val);
            }
            if (typeof val === "object") return JSON.stringify(val);
            return String(val);
          },
        };
      });
  }, [apiFieldNames]);

  const handleAddSubscriberSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();

    const trimmedEmail = newsletterEmail.trim();
    if (!trimmedEmail) {
      setEmailError(t("lang_email_required") || "Email address is required");
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      setEmailError(t("lang_invalid_email") || "Invalid email address");
      return;
    }

    setAddLoading(true);
    setEmailError("");

    try {
      const res = await API({
        url: "/admin/newsletter/AddEmail",
        method: "post",
        data: {
          newsletter_email: trimmedEmail,
        },
      });

      if (res.status === 200 || res.status === 201 || res.status === 304) {
        setIsNotification(true);
        setNotificationMsg("Subscriber added successfully!");
        setSeverity("success");
        setOpenAddSubscriber(false);
        setNewsletterEmail("");
        GetNewslatterMails();
      }
    } catch (error) {
      setIsNotification(true);
      setNotificationMsg(
        error?.response?.data?.message || error?.message || "Failed to add subscriber"
      );
      setSeverity("error");
    } finally {
      setAddLoading(false);
    }
  };

  const totalRecords = allMail?.data?.totalRecords || 0;
  const currentPage = allMail?.data?.currentPage || page;

  const startRecord =
    limit === "all"
      ? 1
      : totalRecords > 0
        ? (currentPage - 1) * Number(limit) + 1
        : 0;
  const endRecord =
    limit === "all"
      ? totalRecords
      : Math.min(currentPage * Number(limit), totalRecords);

  useEffect(() => {
    replacePageInUrl(page);
  }, [page]);

  useEffect(() => {
    if (!isInitialSortLoaded.current && isUserLoaded) {
      isInitialSortLoaded.current = true;
      if (savedSort && savedSort.sort_by) {
        const isAsc = isAscendingOrder(savedSort.order_by);
        const targetOrderBy = isAsc ? "ASC" : "DESC";
        if (savedSort.sort_by !== sortBy || targetOrderBy !== orderBy.orderByValue) {
          setSortBy(savedSort.sort_by);
          setOrderBy({
            orderByValue: targetOrderBy,
            isDecending: !isAsc,
          });
        }
      }
    }
  }, [savedSort, isUserLoaded, sortBy, orderBy.orderByValue]);

  // Sorting Data
  const SortData = (sort_by, explicitOrder) => {
    setSortBy(sort_by);
    let nextOrder = "DESC";
    if (explicitOrder !== undefined) {
      nextOrder = isAscendingOrder(explicitOrder) ? "ASC" : "DESC";
    } else if (sortBy === sort_by) {
      const isCurrentlyAsc = isAscendingOrder(orderBy);
      nextOrder = isCurrentlyAsc ? "DESC" : "ASC";
    } else {
      nextOrder = "DESC";
    }
    setOrderBy({
      orderByValue: nextOrder,
      isDecending: nextOrder === "DESC",
    });
  };

  const GetNewslatterMails = async () => {
    setPending(true);
    try {
      const payload = {
        subscribe: status,
        order_by: orderBy?.orderByValue,
        sort_by: sortBy,
        page,
        limit,
      };
      const data = await API({
        url: `/admin/newsletter/GetNewsletterEmails`,
        method: "post",
        data: payload,
      });
      if (data.status === 200 || data.status === 304) {
        const result = data?.data;
        setAllMail(result);
        setPending(false);
        if (result?.data?.totalPages > 0 && page > result?.data?.totalPages) {
          setPage(result?.data?.totalPages);
        }
      }
    } catch (error) {
      setPending(false);
      setIsNotification(true);
      setNotificationMsg(error?.message || String(error));
      setSeverity("error");
    }
  };

  useEffect(() => {
    GetNewslatterMails();
  }, [orderBy, sortBy, status, page, limit]);

  const handleUpdate = async (email, value) => {
    try {
      const payload = {
        email: email,
        subscribe: value,
      };
      const data = await API({
        url: "/admin/newsletter/updateNewsletter",
        method: "put",
        data: payload,
      });
      if (data.status === 200 || data.status === 304) {
        GetNewslatterMails();
      }
    } catch (error) {
      setIsNotification(true);
      setNotificationMsg(error?.message || String(error));
      setSeverity("error");
    }
  };

  const tableData = allMail?.data?.records || [];

  const columns = [
    {
      key: "newsletter_email",
      title: t("lang_Emails") || "Emails",
      minWidth: "250px",
      headerClassName: "px-3",
      cellClassName: "px-3",
      render: (row) => (
        <span className="w-full block line-clamp-2 text-sm text-[var(--primary-text-color)] font-medium">
          {row.newsletter_email || "-"}
        </span>
      ),
    },
    {
      key: "createdAt",
      title: t("lang_joined_date") || "Joined Date",
      width: "200px",
      align: "text-right",
      headerClassName: "px-3 text-right whitespace-nowrap",
      cellClassName: "px-3 text-right whitespace-nowrap",
      render: (row) => (
        <span className="flex items-center justify-end w-full text-sm text-[var(--table-text-color)]">
          {formatDate(row?.createdAt)}
        </span>
      ),
    },
    {
      key: "subscribe",
      title: t("lang_restrict") || "Restrict",
      width: "120px",
      align: "text-right",
      headerClassName: "px-3 text-right whitespace-nowrap",
      cellClassName: "px-3 text-right whitespace-nowrap",
      ignoreRowClick: true,
      render: (row) => (
        <label
          className="relative flex justify-end w-full my-0 cursor-pointer"
          onClick={(e) => e.stopPropagation()}
        >
          <FormControlLabel
            name="newsletter"
            checked={!row?.subscribe === true}
            control={<MuiSwitch />}
            onChange={() => handleUpdate(row?.newsletter_email, !row.subscribe)}
          />
        </label>
      ),
    },
    ...dynamicColumns,
  ];

  const handleClose = () => {
    setIsNotification(false);
    setNotificationMsg("");
    setSeverity("");
  };

  const limitOptions = [
    { value: 10, label: "10" },
    ...(totalRecords > 10 || limit === 20 ? [{ value: 20, label: "20" }] : []),
    ...(totalRecords > 20 || limit === 50 ? [{ value: 50, label: "50" }] : []),
    { value: "all", label: t("lang_all") },
  ];

  const statusOptions = [
    { value: "all", label: t("lang_all") },
    { value: "1", label: t("lang_Subscribed") },
    { value: "2", label: t("lang_Restricted") },
  ];

  const tableHeaderFilter = (manageFieldsBtn) => (
    <div className="relative w-full border-b main-border-color secondary-bg-color px-4 py-3 flex items-center justify-between min-h-[50px]">
      <div className="flex items-center gap-2">
        <span className="text-sm font-semibold text-[var(--primary-text-color)]">
          {t("lang_Subscribers")}
        </span>
        {allMail?.data?.totalRecords !== undefined && (
          <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-[var(--focus-tab-color)] text-[var(--table-text-color)]">
            {allMail?.data?.totalRecords}
          </span>
        )}
      </div>
      <div className="flex items-center gap-3">
        <div className="w-[140px]">
          <SelectComponent
            name="status"
            value={status}
            options={statusOptions}
            onChange={(e) => {
              setStatus(e.target.value);
              setPage(1);
            }}
            size="sm"
            disableClearable={true}
          />
        </div>
        <div className="w-[90px]">
          <SelectComponent
            name="limit"
            value={limit}
            options={limitOptions}
            onChange={(e) => {
              setLimit(e.target.value === "all" ? "all" : Number(e.target.value));
              setPage(1);
            }}
            size="sm"
            disableClearable={true}
          />
        </div>
        {manageFieldsBtn}
      </div>
    </div>
  );

  return (
    <>
      {isNotification && notificationMsg && (
        <Notification message={notificationMsg} close={handleClose} severity={severity} />
      )}
      <div className="h-auto overflow-y-auto pb-[64px] px-[30px] pt-5">
        <div className="flex flex-wrap items-end justify-between w-full page-header-pb">
          <div className="flex items-center space-x-2">
            <button
              onClick={(e) => {
                e.preventDefault();
                parentNavigate("/newsletter");
              }}
              className="back-arrow !p-1.5"
            >
              <ArrowLeft />
            </button>
            <div>
              <Breadcrumbs
                crumbs={[
                  { name: t("lang_dashboard"), path: `/dashboard` },
                  // { name: t("lang_newsletter"), path: "/newsletter" },
                  {
                    name: t("lang_Subscribers"),
                    path: "/newsletter/subscribers",
                  },
                ]}
              />
              <h4 className="text-xl md:text-2xl font-semibold text-primary">
                {t("lang_Subscribers")}
              </h4>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <ButtonComponent
              variant="primary"
              size="md"
              icon={<Plus className="w-4 h-4" />}
              onClick={(e) => {
                e.preventDefault();
                setOpenAddSubscriber(true);
              }}
            >
              Add Subscriber
            </ButtonComponent>
            <ButtonComponent
              variant="primary"
              size="md"
              onClick={(e) => {
                e.preventDefault();
                setOpenSendMail(true);
              }}
            >
              {`${t("lang_send")} ${t("lang_mail")}`}
            </ButtonComponent>
          </div>
        </div>
        <div className="flex w-full h-full overflow-hidden relative">
          <div className="w-full">
            <CommonTable
              tableId="newsletter"
              columns={columns}
              data={tableData}
              loading={pending}
              onSort={SortData}
              sortBy={sortBy}
              orderBy={orderBy}
              tableHeader={tableHeaderFilter}
              tableFooter={
                allMail?.data && totalRecords > 0 ? (
                  <PaginationComponent
                    count={allMail?.data?.totalPages || 1}
                    page={page}
                    onChange={(e, v) => setPage(v)}
                    totalRecords={totalRecords}
                    startRecord={startRecord}
                    endRecord={endRecord}
                    limit={limit}
                  />
                ) : null
              }
              containerClassName="newsletter-table sticky-table module-table overflow-x-auto border border-[var(--border-color)] rounded-xl shadow-[var(--shadow-card)]"
              NEWAPI={API}
              tableDefaults={defaultCustomFields?.defaultNewsletterField}
              tableWiseAllowedSortKeys={defaultCustomFields?.NEWSLETTER_SORT_FIELDS}
              userState={userContext}
            />
          </div>
        </div>
        <SendMail openSendMail={openSendMail} setOpenSendMail={setOpenSendMail} />

        {/* ================= ADD SUBSCRIBER MODAL ================= */}
        <Dialog
          open={openAddSubscriber}
          TransitionComponent={Transition}
          onClose={() => !addLoading && setOpenAddSubscriber(false)}
          aria-labelledby="add-subscriber-dialog-title"
          PaperProps={{
            sx: {
              width: "500px",
              maxWidth: "95vw",
              borderRadius: "12px",
              overflow: "hidden",
              backgroundColor: "transparent",
              boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
            },
          }}
        >
          <div className="secondary-bg-color text-primary border main-border-color rounded-xl overflow-hidden flex flex-col shadow-2xl">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b main-border-color">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-[var(--focus-tab-color)] text-[var(--primary-text-color)] border border-[var(--border-color)]">
                  <Plus className="w-5 h-5" />
                </div>
                <div>
                  <h3 id="add-subscriber-dialog-title" className="text-lg font-semibold text-primary leading-snug">
                    Add Subscriber
                  </h3>
                  <p className="text-xs table-text mt-0.5">
                    Enter email address to add a new newsletter subscriber
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => !addLoading && setOpenAddSubscriber(false)}
                className="close-btn"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Form Content */}
            <form onSubmit={handleAddSubscriberSubmit} className="flex flex-col flex-1 overflow-hidden secondary-bg-color">
              <div className="p-6 space-y-4 flex-1 overflow-y-auto">
                <InputComponent
                  id="newsletter_email"
                  name="newsletter_email"
                  type="email"
                  label="Email Address"
                  placeholder="Enter subscriber email"
                  required={true}
                  value={newsletterEmail}
                  onChange={(e) => {
                    setNewsletterEmail(e.target.value);
                    if (emailError) setEmailError("");
                  }}
                  error={emailError}
                  autoFocus
                />
              </div>

              {/* Footer Action Buttons */}
              <div className="flex items-center justify-end gap-3 px-6 py-4 border-t main-border-color">
                <ButtonComponent
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setOpenAddSubscriber(false)}
                  disabled={addLoading}
                >
                  Cancel
                </ButtonComponent>

                <ButtonComponent
                  type="submit"
                  size="sm"
                  loading={addLoading}
                  icon={<Plus className="w-4 h-4" />}
                >
                  Add Subscriber
                </ButtonComponent>
              </div>
            </form>
          </div>
        </Dialog>
      </div>
    </>
  );
}

export default GetMails;
